import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import {
  asActivityType,
  payloadNumber,
  payloadString,
  useActivityFeed,
  type ActivityItem,
} from "@/entities/activity";
import { ROUTES } from "@/shared/config";
import { formatDateTime, formatDay, formatMoney, formatPercent, moneySign } from "@/shared/lib";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Деталь события. Figma: `AI Decision Detail` (25:75) и `Trade Detail`
 * (25:115) — один роут, ветка по `ActivityItem.type` (DESIGN.md §2.4).
 *
 * `GET /activity/{id}` в контракте НЕТ и не планируется: всё, что нужно
 * экрану, уже лежит в элементе ленты. Поэтому событие ищется среди
 * загруженных страниц, а при заходе по прямой ссылке (кэш пуст) страницы
 * догружаются по курсору до находки.
 *
 * Догрузка ограничена: реальные ссылки ведут либо из ленты (кэш уже прогрет),
 * либо из свежего пуша (событие на первой странице). Без предела экран по
 * ссылке на полугодовой давности запись перебрал бы всю ленту.
 */
const MAX_LOOKUP_PAGES = 5;

export function ActivityDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const eventId = Number(id);

  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useActivityFeed("all");

  const items = data?.pages.flatMap((page) => page.items) ?? [];
  const item = items.find((candidate) => candidate.id === eventId);
  const pagesLoaded = data?.pages.length ?? 0;
  const canLookDeeper = !item && hasNextPage && pagesLoaded < MAX_LOOKUP_PAGES;

  useEffect(() => {
    if (canLookDeeper && !isFetchingNextPage) void fetchNextPage();
  }, [canLookDeeper, isFetchingNextPage, fetchNextPage]);

  if (!Number.isFinite(eventId)) {
    return (
      <ScreenState tone="info" icon="questionMark" title={t("activity.notFound.title")} body={t("activity.notFound.body")} />
    );
  }

  if (isPending || (canLookDeeper && isFetchingNextPage)) return <ScreenSkeleton blocks={[120, 160]} />;

  if (isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{ label: t("states.error.action"), onClick: () => void refetch() }}
      />
    );
  }

  if (!item) {
    return (
      <ScreenState
        tone="info"
        icon="questionMark"
        title={t("activity.notFound.title")}
        body={t("activity.notFound.body")}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("activity.detailTitle")} subtitle={formatDateTime(item.created_at)} />

      <section className="flex flex-col gap-2 rounded-3xl border border-border-subtle bg-background-elevated p-5">
        <h2 className="text-h3 font-semibold text-text-primary">{item.title}</h2>
        <p className="text-caption font-normal text-text-secondary">{item.body}</p>
      </section>

      <EventFacts item={item} items={items} />
    </div>
  );
}

/**
 * Таблица полей события. Для `robot_purchased`, `deposit`, `withdrawal` и
 * `referral` отдельных макетов нет — переиспользуется каркас `Trade Data`
 * (25:125) как обобщённый блок «поле → значение».
 */
function EventFacts({ item, items }: { item: ActivityItem; items: ActivityItem[] }) {
  const { t } = useTranslation();
  const type = asActivityType(item.type);

  const date = payloadString(item.payload, "date");
  const userRobotId = payloadNumber(item.payload, "user_robot_id");
  const depositId = payloadNumber(item.payload, "deposit_id");
  const withdrawalId = payloadNumber(item.payload, "withdrawal_id");

  // Джоб пишет два события на робота в день: у `ai_decision` цифр нет, они
  // лежат в парном `daily_result` за ту же дату (BE-07). Берём их оттуда,
  // а не показываем решение без результата.
  const sibling =
    type === "ai_decision" && userRobotId !== undefined && date !== undefined
      ? items.find(
          (candidate) =>
            candidate.type === "daily_result" &&
            payloadNumber(candidate.payload, "user_robot_id") === userRobotId &&
            payloadString(candidate.payload, "date") === date,
        )
      : undefined;

  const source = sibling ?? item;
  const percent = payloadString(source.payload, "percent");
  const amount = payloadString(source.payload, "amount");

  const rows: { label: string; value: string }[] = [];
  if (date) rows.push({ label: t("activity.facts.date"), value: formatDay(date) });
  if (percent) rows.push({ label: t("activity.facts.percent"), value: formatPercent(percent, { sign: "always" }) });

  return (
    <>
      {amount ? (
        <section className="flex flex-col gap-1 rounded-2xl border border-border-subtle bg-background-elevated p-4">
          <span className="text-caption font-medium text-text-secondary">
            {t("activity.facts.amount")}
          </span>
          <span
            className={[
              "text-h2 font-bold",
              moneySign(amount) < 0 ? "text-status-loss" : "text-status-profit",
            ].join(" ")}
          >
            {formatMoney(amount, { sign: "always", suffix: "USDT" })}
          </span>
        </section>
      ) : null}

      {rows.length > 0 ? (
        <dl className="flex flex-col gap-3 rounded-2xl border border-border-subtle bg-background-elevated p-4">
          {rows.map((row) => (
            <div key={row.label} className="flex items-start justify-between gap-3">
              <dt className="text-caption font-medium text-text-secondary">{row.label}</dt>
              <dd className="text-label font-semibold text-text-primary">{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {userRobotId !== undefined ? (
        <Link
          to={ROUTES.myRobot(userRobotId)}
          className="rounded-2xl border border-border-subtle bg-background-elevated p-4 text-center text-caption font-semibold text-action-primary"
        >
          {t("activity.openRobot")} →
        </Link>
      ) : null}

      {depositId !== undefined ? (
        <Link
          to={ROUTES.deposit(depositId)}
          className="rounded-2xl border border-border-subtle bg-background-elevated p-4 text-center text-caption font-semibold text-action-primary"
        >
          {t("activity.openDeposit")} →
        </Link>
      ) : null}

      {withdrawalId !== undefined ? (
        <Link
          to={ROUTES.withdrawal(withdrawalId)}
          className="rounded-2xl border border-border-subtle bg-background-elevated p-4 text-center text-caption font-semibold text-action-primary"
        >
          {t("activity.openWithdrawal")} →
        </Link>
      ) : null}
    </>
  );
}
